import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { DateTime } from 'luxon';
import {
  AlertCircle,
  Calendar,
  CalendarClock,
  ChevronRight,
  Clock,
  ListTodo,
  Quote,
  Sun,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCurrentUser } from '@/features/auth/useAuth';
import { planChipDragStyles } from '@/features/calendars/PlanBacklog';
import { ConflictChoiceSheet } from '@/features/calendars/ConflictChoiceSheet';
import { PlanEventSheet, type PlanEvent } from '@/features/calendars/PlanEventSheet';
import { ScheduleSheet } from '@/features/calendars/ScheduleSheet';
import type { BacklogSort } from '@/features/calendars/backlogSort';
import type { TimeBlock } from '@/features/calendars/types';
import { useTimeBlocks } from '@/features/calendars/useTimeBlocks';
import { QuickAdd } from '@/features/tasks/QuickAdd';
import { TaskDetail } from '@/features/tasks/TaskDetail';
import type { Task } from '@/features/tasks/types';
import { useClients, useProjects, useTask, useTasks } from '@/features/tasks/useTasks';
import { formatDuration } from '@/features/tasks/tree';
import { useFocus } from '@/features/time/useFocus';
import { listStartableBlocks } from '@/features/time/pickStartableBlock';
import {
  useWorkBlockControls,
  useWorkBlocks,
} from '@/features/time/useWorkBlocks';
import { DayCalendar } from './DayCalendar';
import { DailyBacklog } from './DailyBacklog';
import {
  formatTodayHeading,
  greetingForHour,
  greetingName,
  todayRange,
} from './greeting';
import { TodayAgendaList } from './TodayAgendaList';
import { countAgendaStats, computeScheduledMinutes, isDueTodayTask } from './todayAgenda';
import { useDailyGridScheduling } from './useDailyGridScheduling';
import { useDailyQuote } from './useDailyQuote';

const SORT_KEY = 'chronos.daily.backlog-sort';

function initialSort(): BacklogSort {
  const stored = localStorage.getItem(SORT_KEY);
  const allowed: BacklogSort[] = ['due', 'effort', 'priority', 'client', 'project'];
  return allowed.includes(stored as BacklogSort) ? (stored as BacklogSort) : 'due';
}

interface DailyPageProps {
  onOpenPlan?: () => void;
  onExpandWorkBlock?: () => void;
}

/**
 * Daily dashboard: default landing after sign-in.
 */
export function DailyPage({ onOpenPlan, onExpandWorkBlock }: DailyPageProps) {
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const quote = useDailyQuote();
  const focus = useFocus();
  const workBlocks = useWorkBlocks();
  const wbControls = useWorkBlockControls();

  const range = useMemo(() => todayRange(timezone), [timezone]);
  const blocks = useTimeBlocks(range);
  // Include closed so a completed block can still open TaskDetail / be unmarked.
  const tasks = useTasks({ include_closed: true });
  const clients = useClients();
  const projects = useProjects();
  const overdue = useTasks({ overdue_only: true });

  const events = useQuery({
    queryKey: ['calendars', 'events', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<PlanEvent[]>(
        `/api/calendars/events?ends_after=${encodeURIComponent(range.start.toISOString())}` +
          `&starts_before=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const busy = useQuery({
    queryKey: ['calendars', 'busy', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<{ starts_at: string; ends_at: string }[]>(
        `/api/calendars/busy?start=${encodeURIComponent(range.start.toISOString())}` +
          `&end=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const [selectedEvent, setSelectedEvent] = useState<PlanEvent | null>(null);
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const [selectedBacklogTask, setSelectedBacklogTask] = useState<Task | null>(null);
  const [schedulingTask, setSchedulingTask] = useState<Task | null>(null);
  const [backlogSort, setBacklogSort] = useState<BacklogSort>(initialSort);

  const scheduling = useDailyGridScheduling({
    blocks: blocks.data ?? [],
    busy: busy.data ?? [],
    tasks: tasks.data ?? [],
    timezone,
  });

  const hour = DateTime.now().setZone(timezone).hour;

  const greeting = greetingForHour(hour);
  const displayName = user ? greetingName(user) : 'there';
  const dateLabel = formatTodayHeading(timezone);

  const listedTask = tasks.data?.find((t) => t.id === openTaskId) ?? null;
  // A subtask opened from its parent's sheet may be deferred, and so absent
  // from this list. Fetch it rather than closing the sheet under the user.
  const fetchedTask = useTask(listedTask || openTaskId == null ? null : openTaskId);
  const openTask = listedTask ?? fetchedTask.data ?? null;

  const dueTodayTasks = useMemo(
    () => (tasks.data ?? []).filter((t) => isDueTodayTask(t, timezone)),
    [tasks.data, timezone],
  );

  const { plannedTasks, appointments } = countAgendaStats(
    blocks.data ?? [],
    events.data ?? [],
  );

  const scheduledMinutes = computeScheduledMinutes(blocks.data ?? [], events.data ?? [], {
    timezone,
    dayStartMinute: user?.day_start_minute ?? 420,
    dayEndMinute: user?.day_end_minute ?? 1200,
    dueTodayTasks,
    overdueTasks: overdue.data ?? [],
  });

  const pomodorosDone = focus.data?.pomodoros_completed_today ?? 0;
  const pomodoroGoal = focus.data?.daily_goal_pomodoros ?? 0;

  const colorFor = useCallback(
    (clientId: number) =>
      clients.data?.find((c) => c.id === clientId)?.color ?? 'var(--border)',
    [clients.data],
  );

  const onBlockClick = (block: TimeBlock) => {
    setOpenTaskId(block.task_id);
  };

  const openTaskFromChip = (task: Task) => {
    setSelectedBacklogTask(task);
    setOpenTaskId(task.id);
  };

  const agendaLoading =
    (blocks.isPending && !blocks.data) ||
    (events.isPending && !events.data) ||
    (tasks.isPending && !tasks.data);

  const calendarScheduling = useMemo(
    () => ({
      dragConflicts: scheduling.dragConflicts,
      onDrop: scheduling.onDrop,
      onEventDrop: scheduling.onEventDrop,
      onEventResize: scheduling.onEventResize,
      onEventDrag: scheduling.onEventDrag,
      onEventDragStart: scheduling.onEventDragStart,
      onEventDragStop: scheduling.onEventDragStop,
    }),
    [scheduling],
  );

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-6">
      <style>{planChipDragStyles}</style>

      <header className="mb-6">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-quiet text-accent"
            aria-hidden
          >
            <Sun size={20} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-title font-semibold tracking-tight">
              {greeting}, {displayName}
            </h1>
            <p className="mt-0.5 text-body text-text-secondary">{dateLabel}</p>
          </div>
        </div>

        <figure className="mt-4 hidden rounded-lg border border-border bg-surface-2/60 px-4 py-3 sm:block">
          <div className="flex gap-3">
            <Quote
              size={16}
              strokeWidth={1.5}
              className="mt-0.5 shrink-0 text-accent"
              aria-hidden
            />
            <blockquote className="min-w-0">
              <p className="text-body italic text-text-secondary">
                &ldquo;{quote.data?.text}&rdquo;
              </p>
              <figcaption className="mt-1.5 text-small text-text-tertiary">
                - {quote.data?.author}
              </figcaption>
            </blockquote>
          </div>
        </figure>
      </header>

      <div className="mb-5 hidden grid-cols-2 gap-3 sm:grid sm:grid-cols-4">
        <StatCard
          icon={<ListTodo size={16} strokeWidth={1.5} aria-hidden />}
          label="Tasks planned"
          value={String(plannedTasks)}
        />
        <StatCard
          icon={<Calendar size={16} strokeWidth={1.5} aria-hidden />}
          label="Appointments"
          value={String(appointments)}
        />
        <StatCard
          icon={<CalendarClock size={16} strokeWidth={1.5} aria-hidden />}
          label="Scheduled time"
          value={formatDuration(scheduledMinutes) ?? '-'}
          primary
        />
        <StatCard
          icon={<Clock size={16} strokeWidth={1.5} aria-hidden />}
          label="Pomodoros"
          value={
            pomodoroGoal > 0 ? `${pomodorosDone} / ${pomodoroGoal}` : String(pomodorosDone)
          }
        />
        {(overdue.data?.length ?? 0) > 0 && (
          <StatCard
            icon={<AlertCircle size={16} strokeWidth={1.5} aria-hidden />}
            label="Overdue"
            value={String(overdue.data?.length ?? 0)}
            tone="warning"
            className="sm:col-span-2"
          />
        )}
      </div>

      {/* Mobile order: work → agenda → calendar → quick add → backlog.
          Desktop keeps the two-column split with calendar sticky on the right. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <WorkBlockHero
          className="order-1 lg:order-none lg:col-start-1"
          blocks={workBlocks.data ?? []}
          activeBlockId={focus.data?.active_work_block_id ?? null}
          timezone={timezone}
          onStart={() => {
            wbControls.create.mutate(
              { origin: 'daily_start' },
              {
                onSuccess: (block) => {
                  api<unknown>(`/api/work-blocks/${block.id}/start`, { method: 'POST' })
                    .then(() => void focus.refetch())
                    .catch(() => {
                      toast.error(
                        'Block was created but could not start, open it from Plan or try again',
                      );
                      void workBlocks.refetch();
                    });
                },
              },
            );
          }}
          onStartPlanned={(blockId) => {
            api<unknown>(`/api/work-blocks/${blockId}/start`, { method: 'POST' })
              .then(() => void focus.refetch())
              .catch(() => {
                toast.error('Could not start that block');
              });
          }}
          onContinue={() => {
            onExpandWorkBlock?.();
            void focus.refetch();
          }}
        />

        <section className="order-2 rounded-lg border border-border bg-surface-1 lg:order-none lg:col-start-1">
          <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <h2 className="text-body font-semibold">Today&apos;s agenda</h2>
              <p className="text-small text-text-secondary">
                {plannedTasks} task{plannedTasks === 1 ? '' : 's'} · {appointments}{' '}
                appointment{appointments === 1 ? '' : 's'}: in schedule order
              </p>
            </div>
            {onOpenPlan && (
              <Button variant="ghost" onClick={onOpenPlan} className="shrink-0 text-small">
                Plan week
                <ChevronRight size={14} strokeWidth={1.5} aria-hidden />
              </Button>
            )}
          </header>

          <div className="p-2">
            <TodayAgendaList
              timezone={timezone}
              onOpenTask={setOpenTaskId}
              onOpenEvent={setSelectedEvent}
              onOpenBlock={onBlockClick}
              onOpenPlan={onOpenPlan}
              loading={agendaLoading}
            />
          </div>
        </section>

        <DayCalendar
          className="order-3 lg:order-none lg:col-start-2 lg:row-span-4 lg:row-start-1"
          onEventClick={setSelectedEvent}
          onBlockClick={onBlockClick}
          scheduling={calendarScheduling}
        />

        <section className="order-4 rounded-lg border border-border bg-surface-1 p-4 lg:order-none lg:col-start-1">
          <h2 className="mb-3 text-body font-semibold">Quick capture</h2>
          <QuickAdd />
        </section>

        <div className="order-5 lg:order-none lg:col-start-1">
          <DailyBacklog
            tasks={tasks.data ?? []}
            clients={clients.data ?? []}
            projects={projects.data ?? []}
            timezone={timezone}
            sort={backlogSort}
            onSortChange={(next) => {
              setBacklogSort(next);
              localStorage.setItem(SORT_KEY, next);
            }}
            clientColor={colorFor}
            selectedTaskId={selectedBacklogTask?.id ?? null}
            onSelectTask={openTaskFromChip}
            onSchedule={setSchedulingTask}
            dropActive={scheduling.backlogDropActive}
            loading={tasks.isPending && !tasks.data}
            loadError={tasks.isError && !tasks.data}
            onRetry={() => void tasks.refetch()}
          />
        </div>
      </div>

      <AnimatePresence>
        {openTask && (
          <TaskDetail
            task={openTask}
            onClose={() => setOpenTaskId(null)}
            onOpenTask={setOpenTaskId}
          />
        )}
      </AnimatePresence>

      {selectedEvent && (
        <PlanEventSheet
          event={selectedEvent}
          timezone={timezone}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {schedulingTask && (
        <ScheduleSheet
          task={schedulingTask}
          timezone={timezone}
          onClose={() => setSchedulingTask(null)}
          submitting={scheduling.isScheduling}
          onSubmit={(startsAt, endsAt) => {
            scheduling.scheduleTask(schedulingTask, startsAt, endsAt);
            setSchedulingTask(null);
          }}
        />
      )}

      {scheduling.pendingPlacement && (
        <ConflictChoiceSheet
          taskTitle={scheduling.pendingPlacement.taskTitle}
          startsAt={scheduling.pendingPlacement.startsAt}
          endsAt={scheduling.pendingPlacement.endsAt}
          timezone={timezone}
          canSplit={scheduling.pendingCanSplit}
          onClose={() => scheduling.setPendingPlacement(null)}
          onChoose={(choice) => {
            if (choice.mode === 'overlap') {
              scheduling.commitOverlap(scheduling.pendingPlacement!);
            } else {
              void scheduling.commitSplit(scheduling.pendingPlacement!);
            }
          }}
        />
      )}
    </div>
  );
}

function WorkBlockHero({
  blocks,
  activeBlockId,
  timezone,
  onStart,
  onStartPlanned,
  onContinue,
  className,
}: {
  blocks: import('@/features/time/useWorkBlocks').WorkBlock[];
  activeBlockId: number | null;
  timezone: string;
  onStart: () => void;
  onStartPlanned: (blockId: number) => void;
  onContinue: () => void;
  className?: string;
}) {
  const now = Date.now();
  const active =
    blocks.find((b) => b.status === 'active') ??
    blocks.find((b) => b.id === activeBlockId);
  // Same grace + ordering as the server / Mac so Daily cannot offer a block
  // that start_now would skip, or hide one still within grace.
  const startable = listStartableBlocks(blocks, now);

  return (
    <section className={cn('rounded-lg border border-accent-border bg-accent-quiet p-4', className)}>
      <h2 className="text-body font-semibold">Work block</h2>
      {active ? (
        <>
          <p className="mt-1 text-small text-text-secondary">
            Block in progress · {active.phase.replace('_', ' ')}
          </p>
          <Button className="mt-3" onClick={() => onContinue()}>
            Continue block
          </Button>
        </>
      ) : (
        <>
          <p className="mt-1 text-small text-text-secondary">
            {startable.length > 0
              ? 'Start a planned focus window, or begin a new one now.'
              : 'Start a 2-hour focus session with planned tasks and Pomodoro sprints.'}
          </p>
          {startable.length > 0 && (
            <ul className="mt-3 flex flex-col gap-2">
              {startable.map((b) => (
                <li key={b.id}>
                  <Button
                    variant="secondary"
                    className="w-full justify-between"
                    onClick={() => onStartPlanned(b.id)}
                  >
                    <span>
                      {DateTime.fromISO(b.starts_at, { zone: 'utc' })
                        .setZone(timezone)
                        .toLocaleString(DateTime.TIME_SIMPLE)}
                      {' – '}
                      {DateTime.fromISO(b.ends_at, { zone: 'utc' })
                        .setZone(timezone)
                        .toLocaleString(DateTime.TIME_SIMPLE)}
                    </span>
                    <span className="text-small">
                      {new Date(b.starts_at).getTime() < now ? 'Start late' : 'Start'}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <Button className="mt-3" variant={startable.length > 0 ? 'ghost' : 'primary'} onClick={onStart}>
            {startable.length > 0 ? 'Or start a new block now' : 'Start work block'}
          </Button>
        </>
      )}
    </section>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone = 'default',
  primary = false,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'default' | 'warning';
  primary?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3',
        tone === 'warning'
          ? 'border-warning-quiet bg-warning-quiet'
          : primary
            ? 'border-accent-border bg-accent-quiet'
            : 'border-border bg-surface-1',
        className,
      )}
    >
      <div
        className={cn(
          'mb-1 flex items-center gap-1.5 text-small',
          tone === 'warning' ? 'text-warning' : primary ? 'text-accent' : 'text-text-secondary',
        )}
      >
        {icon}
        {label}
      </div>
      <p
        className={cn(
          'tabular font-semibold',
          primary ? 'text-heading' : 'text-body',
        )}
      >
        {value}
      </p>
    </div>
  );
}
